import {noop} from '@benchling/js-utils';
import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {v4 as uuidv4} from 'uuid';

import {isFileConfigVariable} from 'analysis-tool/templates/AnalysisTemplateVariableUtils';
import {trackEvent} from 'analytics/Analytics';
import {assert} from 'Asserts';
import {RESULTS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {Tag} from 'folder-items/FileConstants';
import {Status as AutomationFileStatusGraphQL} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {restToGraphQL} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {InstrumentServiceLoadStatus} from 'instruments/connections/ConnectionConstants';
import RunAutomationFileViewer from 'lab-automation/automation-files/RunAutomationFileViewer';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import {ActionFunctionContext} from 'notebook/structured-tables/action-functions/ActionFunctionContext';
import {BenchlingState} from 'redux-stores/reduceApp';
import {
  GetAnalysisTemplateForAssayRun,
  GetAnalysisTemplateForAssayRunVariables,
  GetAnalysisTemplateForAssayRun_analysisTemplate_AnalysisTemplateNode as AnalysisTemplateNode,
} from 'results/analysis-integration/__generated/GetAnalysisTemplateForAssayRun';
import {GET_ANALYSIS_TEMPLATE_FOR_ASSAY_RUN_QUERY} from 'results/analysis-integration/AnalysisTemplateIntegrationUtils';
import {AssayRunSchema, AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import {createAssayRunAndPatchEntry} from 'results/assays/AssayRecordActions';
import RunsInboxModal from 'runs/inbox/RunsInboxModal';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {getTagsToSet} from 'schemas/tags/TagUtil';
import ModalAnchor from 'ui-library/ModalAnchor';
import APIUtil from 'util/APIUtil';
import memoizeLastCall from 'util/memoizeLastCall';

import {AutomationErrorFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationErrorFileGraphQL';
import {AutomationInputFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationInputFileGraphQL';
import {AutomationOutputFileGraphQL} from '../../../graphql-fragments/lab-automation/__generated/AutomationOutputFileGraphQL';
import {DisplayConfig} from '../AttachmentConstants';
import {makeDisplayConfigFromRunSchema} from '../MakeDisplayConfig';
import {FileVariableBinding, InputFileFields} from './AnalysisTemplateIntegrationRun';
import {AssayGroupHeader} from './AssayGroup';
import AssayGroupContainer from './AssayGroupContainer';
import {AddRunToolbar, validateRunForSubmit} from './AssayGroupEditButtons';
import AssayRunFields from './AssayRunFields';
import {computeVisibleRunFields} from './VisibleRunFields';

type PlaceholderAutomationOutputFileGraphQL = Partial<AutomationOutputFileGraphQL> & {
  __typename: 'AutomationOutputFileNode';
};

const createPlaceholderAutomationFiles = memoizeLastCall((runSchema: AssayRunSchema) => {
  const automationInputFiles: Array<AutomationInputFileGraphQL> = runSchema.automationInputFileConfigs.map(
    (config) =>
      ({
        __typename: 'AutomationInputFileNode',
        id: '',
        status: AutomationFileStatusGraphQL.pending,
        blob: null,
        modifiedAt: new Date().toString(),
        transformFiles: [],
        automationInputFileConfig: {
          __typename: 'AutomationInputFileConfigNode',
          ...config,
          isHidden: Boolean(config.isHidden),
        },
        errors: null,
      }) satisfies AutomationInputFileGraphQL
  );

  const automationOutputFiles: Array<PlaceholderAutomationOutputFileGraphQL> =
    runSchema.automationOutputFileConfigs.map((config) => ({
      __typename: 'AutomationOutputFileNode',
      id: '',
      runId: '',
      status: AutomationFileStatusGraphQL.pending,
      blob: null,
      automationOutputFileConfig: restToGraphQL('AutomationOutputFileConfig', config),
      errors: null,
      name: '',
      createdFile: null,
    }));

  const automationErrorFiles: Array<AutomationErrorFileGraphQL> = runSchema.automationOutputFileConfigs.map(
    (config) => ({
      __typename: 'AutomationErrorFileNode',
      id: '',
      status: AutomationFileStatusGraphQL.pending,
      blob: null,
      automationOutputFileConfig: restToGraphQL('AutomationOutputFileConfig', config),
    })
  );

  return {
    automationInputFiles,
    automationOutputFiles: automationOutputFiles as Array<AutomationOutputFileGraphQL>,
    automationErrorFiles,
  };
});

interface BaseEmptyAssayGroupProps {
  assayRunSchemaInfo: {
    assayRunSchema: AssayRunSchema;
    versionedName: string | null;
    versionedFields: Array<SchemaField> | null;
  };
  assayRunInfo?: {
    assayRunId: string | null;
    versionedTags: Array<Tag> | null;
    versionedConnectionId: string | null;
    versionedEquipmentId: string | null;
  };
  children?: React.ReactNode;
  editButtons?: React.ReactNode;
  indentation: number;
  cid: string;
  isLoading?: boolean;
  locked: boolean;

  // If true, the Notebook is in print-preview or export mode
  viewer?: boolean;
}

/** Get bindings for view-only file inputs of analysis template. */
function getBindingsForTemplate(analysisTemplate: AnalysisTemplateNode): Array<FileVariableBinding> {
  return analysisTemplate.configVariables
    .filter((variable) => isFileConfigVariable(variable))
    .map(({id}) => ({variableId: id, variableValue: null}));
}

export function BaseEmptyAssayGroup(props: BaseEmptyAssayGroupProps): JSX.Element {
  const displayAnalysisTemplateIntegrationUI =
    props.assayRunSchemaInfo.assayRunSchema.analysisTemplate?.id != null;

  const getAnalysisTemplateResponse = useSkippableBenchlingQuery<
    GetAnalysisTemplateForAssayRun,
    GetAnalysisTemplateForAssayRunVariables
  >(GET_ANALYSIS_TEMPLATE_FOR_ASSAY_RUN_QUERY, {
    variables: {analysisTemplateId: props.assayRunSchemaInfo.assayRunSchema.analysisTemplate?.id!},
    skip: !displayAnalysisTemplateIntegrationUI,
  });
  let analysisTemplate: AnalysisTemplateNode | null = null;
  if (
    getAnalysisTemplateResponse &&
    getAnalysisTemplateResponse.type === QueryResultType.SUCCESS &&
    !isInaccessible(getAnalysisTemplateResponse.data.analysisTemplate)
  ) {
    analysisTemplate = getAnalysisTemplateResponse.data.analysisTemplate;
  }

  const connectionSchema = props.assayRunSchemaInfo.assayRunSchema.connectionSchema;
  return (
    <AssayGroupContainer
      assayRunIdForValidationFetch={null}
      indentation={props.indentation}
      isLoading={props.isLoading}
      title={
        <AssayGroupHeader
          displayAnalysisTemplateIntegrationUI={displayAnalysisTemplateIntegrationUI}
          editButtons={!props.locked && props.editButtons}
          titleText={props.assayRunSchemaInfo.assayRunSchema.name}
        />
      }
      showValidationSideBar={true}
    >
      {props.children}
      <AssayRunFields
        displayAnalysisTemplateIntegrationUI={displayAnalysisTemplateIntegrationUI}
        liveFields={computeVisibleRunFields(props.assayRunSchemaInfo.assayRunSchema)}
        versionedFields={props.assayRunSchemaInfo.versionedFields}
        versionedTags={props.assayRunInfo ? props.assayRunInfo.versionedTags : null}
        cid={props.cid}
        isEditable={!props.locked && props.assayRunSchemaInfo.assayRunSchema.allowCreateRunFromNlad}
        adapterId={props.assayRunSchemaInfo.assayRunSchema.adapterId}
        versionedConnectionId={props.assayRunInfo ? props.assayRunInfo.versionedConnectionId : null}
        versionedEquipmentId={props.assayRunInfo ? props.assayRunInfo.versionedEquipmentId : null}
        shouldRecordEquipment={props.assayRunSchemaInfo.assayRunSchema.shouldRecordEquipment}
        hasAutomationFiles={false}
        viewer={props.viewer}
      />
      {displayAnalysisTemplateIntegrationUI && analysisTemplate != null && (
        <InputFileFields
          analysisTemplate={analysisTemplate}
          runId="run-preview"
          fileVariableBindings={getBindingsForTemplate(analysisTemplate)}
          setFileVariableBindings={noop}
          defaultFiltersMapping={null}
          isDisabled={true}
        />
      )}
      <RunAutomationFileViewer
        runId={null}
        isConnectionRun={connectionSchema != null}
        connectionId={null}
        automationFiles={createPlaceholderAutomationFiles(props.assayRunSchemaInfo.assayRunSchema)}
        locked={true}
        handleUpdateAutomationFiles={() => {
          assert(false, 'Tried to update automation files for empty run');
        }}
        handleRefreshVersionedData={() => {
          assert(false, 'Tried to resync empty run');
        }}
        instrumentServiceDetails={{status: InstrumentServiceLoadStatus.UNSET, data: null}}
      />
    </AssayGroupContainer>
  );
}

BaseEmptyAssayGroup.defaultProps = {
  isLoading: false,
};

interface OwnProps {
  assayRunSchemaInfo: {
    assayRunSchema: AssayRunSchema;
    versionedName: string | null;
    versionedFields: Array<SchemaField> | null;
  };
  assayRunInfo: {
    assayRunId: string | null;
    versionedTags: Array<Tag> | null;
    versionedConnectionId: string | null;
    versionedEquipmentId: string | null;
  };
  handleUpdateTags: (
    tags: Array<Tag> | null,
    connectionId: string | null,
    equipmentId: string | null
  ) => void;
  cid: string;
  entryId: string;
  provenanceUuid: string;
  handleSetAssayRun: (
    selectedRunId: string,
    assayResultSchemas: Array<AssaySchema>,
    runSchemaId: string,
    runDisplayConfig: DisplayConfig
  ) => Promise<void>;
  // Will be called if the provenanceUuid for the NLAD is null due to the backfill not yet running
  handleSetProvenanceUuid: (provenanceUuid: string) => void;
  indentation: number;
  locked: boolean;
  // While creating an assay run is not technically a structured table action function, it requires the same context for recording it's creation context
  actionFunctionContext: ActionFunctionContext;

  // If true, the Notebook is in print-preview or export mode
  viewer?: boolean;
}

interface ConnectedProps {
  tags: Array<Tag> | null | undefined;
  connectionId: string | null;
  equipmentId: string | null;
  studyIds: Array<string>;
}

type EmptyAssayGroupProps = OwnProps & ConnectedProps;

/**
 * Component for interacting with an empty run component.
 *
 * Supports populating the empty run via manual creation or selecting one from the runs inbox.
 */
export function EmptyAssayGroup(props: EmptyAssayGroupProps): JSX.Element {
  const [isSaving, setIsSaving] = useState(false);
  const [isRunsInboxModalOpen, setIsRunsInboxModalOpen] = useState(false);
  const {isConfig: configFromContext} = EditorPropertiesContext.useContext();
  const isConfig = configFromContext || APIUtil.isEntryOrSubOrWorkflowTemplate(props.entryId);

  const _handleOpenRunsInboxModal: () => void = () => {
    trackEvent(RESULTS_ANALYTICS_ACTIONS.OPENED_RUN_INBOX, {
      run_schema_api_id: props.assayRunSchemaInfo.assayRunSchema.id,
      entry_id: props.entryId,
    });
    setIsRunsInboxModalOpen(true);
  };

  const _handleCloseRunsInboxModal: () => void = () => {
    setIsRunsInboxModalOpen(false);
  };

  const _handleSetAssayRun = async (
    selectedRunId: string,
    resultSchemas: Array<AssaySchema>
  ): Promise<void> => {
    await props.handleSetAssayRun(
      selectedRunId,
      resultSchemas,
      props.assayRunSchemaInfo.assayRunSchema.id,
      makeDisplayConfigFromRunSchema(props.assayRunSchemaInfo.assayRunSchema)
    );
  };

  const _onCreate = async (): Promise<void> => {
    setIsSaving(true);
    // Backfill the provenanceUuid before we attempt to use it, in case it was null
    const noteLinkedAssayDataProvenanceUuid = props.provenanceUuid || uuidv4();
    const backfillProvenanceUuidFn = (): void => {
      if (!props.provenanceUuid) {
        props.handleSetProvenanceUuid(noteLinkedAssayDataProvenanceUuid);
      }
    };
    try {
      await createAssayRunAndPatchEntry({
        assayRunSchemaId: props.assayRunSchemaInfo.assayRunSchema.id,
        entryOrTemplateId: props.entryId,
        noteLinkedAssayDataProvenanceUuid,
        tags: getTagsToSet(props.tags || []),
        connectionId: props.connectionId,
        equipmentId: props.equipmentId,
        studyIds: props.studyIds,
        backfillProvenanceUuidFn,
        actionFunctionContext: props.actionFunctionContext,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isConfig) {
    return <EmptyAssayGroupTemplate {...props} />;
  }
  return (
    <BaseEmptyAssayGroup
      assayRunSchemaInfo={props.assayRunSchemaInfo}
      assayRunInfo={props.assayRunInfo}
      editButtons={
        <AddRunToolbar
          runSchema={props.assayRunSchemaInfo.assayRunSchema}
          onClickCreate={_onCreate}
          onClickInsertFromInbox={_handleOpenRunsInboxModal}
          validateForSubmit={() =>
            validateRunForSubmit(
              props.tags,
              props.connectionId,
              props.assayRunSchemaInfo.assayRunSchema.connectionSchema
            )
          }
        />
      }
      indentation={props.indentation}
      cid={props.cid}
      isLoading={isSaving}
      locked={props.locked}
      viewer={props.viewer}
    >
      <ModalAnchor key="inbox-modal" open={isRunsInboxModalOpen}>
        <RunsInboxModal
          assayRunSchema={props.assayRunSchemaInfo.assayRunSchema}
          entryId={props.entryId}
          onInsertAssayRun={_handleSetAssayRun}
          onRequestClose={_handleCloseRunsInboxModal}
        />
      </ModalAnchor>
    </BaseEmptyAssayGroup>
  );
}

function mapStateToProps(state: BenchlingState, ownProps: OwnProps): ConnectedProps {
  return {
    tags: state.ui.schemaTags.tagsByCid[ownProps.cid],
    connectionId: state.ui.schemaTags.connectionIdByCid[ownProps.cid] ?? null,
    equipmentId: state.ui.schemaTags.equipmentIdByCid[ownProps.cid] ?? null,
    studyIds: state.ui.schemaTags.studyIdsByCid[ownProps.cid] ?? [],
  };
}

export default connect(mapStateToProps)(EmptyAssayGroup);

/**
 * Component to render empty run in template
 */
function BaseEmptyAssayGroupTemplate(props: EmptyAssayGroupProps): JSX.Element {
  const {tags, handleUpdateTags, connectionId, locked} = props;

  // this is used to persist fields on a run, particularly needed for runs in entry templates
  useEffect(() => {
    if (!locked) {
      handleUpdateTags(tags ?? null, connectionId, null);
    }
  }, [connectionId, tags, handleUpdateTags, locked]);

  const runSchema = props.assayRunSchemaInfo.assayRunSchema;
  const isLabAutoRun =
    [...runSchema.automationInputFileConfigs, ...runSchema.automationOutputFileConfigs].length > 0;
  return (
    <BaseEmptyAssayGroup
      assayRunSchemaInfo={props.assayRunSchemaInfo}
      assayRunInfo={props.assayRunInfo}
      indentation={props.indentation}
      cid={props.cid}
      isLoading={false}
      locked={locked || !isLabAutoRun}
      viewer={props.viewer}
    />
  );
}

const EmptyAssayGroupTemplate = connect(mapStateToProps)(BaseEmptyAssayGroupTemplate);
